import { Coordinate } from 'ol/coordinate';
import { RosWaypoint } from '../../types/rosmsg';
import { StartAt } from '../../types/rosmsg';
import { convertProjection3857 } from '../../utils';

export interface CalculateWaypointNumberParams {
  startAt: StartAt;
  /** Sample ID, optionally with a core number separated by . , ; or -
   *  e.g. "A", "U.2", "B,3". Only the manual SetWaypoint text field sends core numbers;
   *  map clicks, zone missions, and redo-last-sample always send plain sample IDs. */
  sampleInput: string | null;
  targetWaypointNumber: string | null;
  coordinates?: Coordinate;
  waypoints: RosWaypoint[];
  isZoneMission: boolean;
}

/**
 * Calculates the 1-indexed waypoint index to send to the robot via mission/set_waypoint.
 * Returns null on lookup failure — the caller should warn and abort.
 *
 * The robot expects 1-indexed values and converts with (value - 1).
 *
 * Known issue: sample_id regex /^([a-zA-Z0-9]+).../ does not match underscores,
 * so sample IDs like "A_EXT123" will fail lookup and return null.
 */
export function calculateWaypointNumber(params: CalculateWaypointNumberParams): number | null {
  const { startAt, sampleInput, targetWaypointNumber, coordinates, waypoints, isZoneMission } = params;

  let waypointIndex: number | null = null;

  switch (startAt) {
    case StartAt.GoToStart:
      waypointIndex = 0;
      break;

    case StartAt.GoToSample: {
      // Try coordinate match first — only for zone missions with coordinates
      let coordMatchIdx = -1;

      if (coordinates && isZoneMission) {
        const convertedCoordinates = convertProjection3857(coordinates);
        coordMatchIdx = waypoints.findIndex(
          (wp) =>
            wp.position.x.toFixed(6) === convertedCoordinates[1].toFixed(6) &&
            wp.position.y.toFixed(6) === convertedCoordinates[0].toFixed(6),
        );

        if (coordMatchIdx !== -1) {
          waypointIndex = coordMatchIdx;
        }
      }

      // Sample lookup by id (no coordinates needed)
      if (coordMatchIdx === -1 && sampleInput) {
        const m = sampleInput.match(/^([a-zA-Z0-9]+)([,.;-]([1-9][0-9]*))?$/);
        const sampleId = m?.[1];
        let idx = waypoints.findIndex((wp) => wp.sample_id === sampleId);

        const coreNumber = m?.[3];

        if (idx !== -1 && coreNumber) {
          const coreIdx = idx + parseInt(coreNumber) - 1;
          idx = coreIdx < waypoints.length ? coreIdx : -1;
        }

        if (idx !== -1) {
          waypointIndex = idx;
        }
      }

      break;
    }

    case StartAt.StartAfterSample: {
      const idx = waypoints.findLastIndex((wp) => wp.sample_id === sampleInput);

      if (idx !== -1) {
        waypointIndex = idx + 1;
      }

      break;
    }

    case StartAt.SpecificWaypoint:
      if (targetWaypointNumber) {
        waypointIndex = parseInt(targetWaypointNumber) - 1;
      }

      break;

    case StartAt.GoToEnd:
      waypointIndex = waypoints.length - 1;
      break;
  }

  if (waypointIndex == null || isNaN(waypointIndex) || waypointIndex < 0) {
    return null;
  }

  // We need 1-indexed value: waypointNumber = waypointIndex + 1
  return waypointIndex + 1;
}
